import type { ReactNode } from 'react'
import { MapPin, Music } from 'lucide-react'
import { fbt } from 'fbtee'
import { formatGenres } from '@shared/genres'
import { formatDayShort } from '../../lib/formatDate'
import { cn } from '../../lib/utils'
import type { ArtistNextAppearance } from '../../hooks/useArtistNextAppearances'
import type { Artist, UserProfile } from '../../types'
import { SearchThumb } from '../GlobalSearch/SearchResultRows'
import { MemberFacepile } from '../MemberFacepile'
import { Skeleton } from '../ui/skeleton'

interface Props {
  loading?: boolean
  artistId?: string
  artist?: Artist
  appearance?: ArtistNextAppearance
  friendIds?: string[]
  profiles?: Record<string, UserProfile>
  locale?: string
  /** Dims the row — the viewer just removed this artist and can still undo. */
  dimmed?: boolean
  onOpen?: () => void
  trailing?: ReactNode
}

/** One artist on a profile: photo, name, where they play next (or genres), friends who like them. */
export function ProfileArtistRow({
  loading,
  artistId,
  artist,
  appearance,
  friendIds = [],
  profiles = {},
  locale = 'en_US',
  dimmed,
  onOpen,
  trailing,
}: Props) {
  if (loading) {
    return (
      <div className="flex items-center gap-3 px-2 py-2">
        <Skeleton className="size-10 shrink-0 rounded-full" />
        <div className="flex-1 space-y-1.5">
          <Skeleton className="h-3.5 w-2/5" />
          <Skeleton className="h-3 w-3/5" />
        </div>
      </div>
    )
  }

  const genres = formatGenres(artist?.genres)
  return (
    <div
      className={cn('flex items-center gap-1 rounded-lg transition-opacity', dimmed && 'opacity-50')}
      data-testid="profile-artist-row"
    >
      <button
        type="button"
        onClick={onOpen}
        className="flex min-w-0 flex-1 cursor-pointer items-center gap-3 rounded-lg px-2 py-2 text-left text-sm hover:bg-muted focus-visible:bg-muted focus-visible:outline-none"
      >
        <SearchThumb src={artist?.imageUrl} icon={Music} round />
        <span className="min-w-0 flex-1">
          <span className="block truncate font-medium">
            {artist?.name ?? <Skeleton className="h-3.5 w-24" />}
          </span>
          {appearance ? (
            <span className="flex items-center gap-1 truncate text-xs text-muted-foreground">
              {appearance.near && <MapPin className="size-3 shrink-0 text-primary" />}
              <span className="truncate">
                {fbt(
                  fbt.param('event', appearance.event.name) + ' · ' +
                    fbt.param('date', formatDayShort(appearance.event.startDate.slice(0, 10), locale)),
                  'Subtitle on a profile artist row: the next event the artist plays and its date',
                )}
              </span>
            </span>
          ) : genres ? (
            <span className="block truncate text-xs text-muted-foreground">{genres}</span>
          ) : null}
        </span>
      </button>
      {friendIds.length > 0 && (
        <MemberFacepile
          interestedMemberIds={friendIds}
          goingMemberIds={[]}
          profiles={profiles}
          dialogTitle={artist?.name}
          historyKey={artistId && `profile-artist:${artistId}`}
          max={3}
        />
      )}
      {artistId && trailing}
    </div>
  )
}
